import React, { useState, useEffect } from "react";
import { Select, InputNumber } from "antd";
import { getRooms } from "../../../../apis/room";
import { transferRoom } from "../../../../apis/visit";
import { Visit } from "../../../../interfaces/Visit";
import { useNotification } from "../../../../NotificationContext";

const { Option: SelectOption } = Select;

interface RoomTransferFormProps {
  visit: Visit;
  initialData?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
}

interface Room {
  id: number;
  name: string;
  floor: string;
  status: string;
  roomType: {
    name: string;
    pricePerDay?: number;
  };
}

const RoomTransferForm: React.FC<RoomTransferFormProps> = ({ visit, onSave, onCancel }) => {
  const { showNotification } = useNotification();
  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentRoom, setCurrentRoom] = useState<Room | null>(null);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);

  const [formData, setFormData] = useState({
    toRoomId: null as number | null,
    reason: "",
    billedRatePerDay: null as number | null,
  });

  useEffect(() => {
    fetchRooms();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRooms = async () => {
    const currentRoomId = (visit as any)?.roomId ?? (visit as any)?.room?.id;
    try {
      setLoading(true);
      // Fetch current room info separately (it may show as occupied/unavailable)
      const allRes: any = await getRooms({ per_page: 1000 });
      const allRooms: Room[] = allRes.data || [];
      if (currentRoomId) {
        const room = allRooms.find((r) => r.id === currentRoomId);
        if (room) setCurrentRoom(room);
      }

      // Fetch rooms with at least one free bed for the destination selector
      const availRes: any = await getRooms({ per_page: 1000, has_available_beds: 1 });
      const availRooms: Room[] = availRes.data || [];
      setAvailableRooms(availRooms.filter((r) => r.id !== currentRoomId));
    } catch {
      showNotification("error", "Failed to load rooms", "", "topRight");
      setAvailableRooms([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRoomSelect = (value: number) => {
    const room = availableRooms.find((r) => r.id === value) ?? null;
    setSelectedRoom(room);
    setFormData((prev) => ({
      ...prev,
      toRoomId: value,
      // Pre-fill with room type default
      billedRatePerDay: room?.roomType?.pricePerDay ?? null,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.toRoomId) {
      showNotification("error", "Please select a destination room", "", "topRight");
      return;
    }
    try {
      setSaving(true);
      await transferRoom(visit.id!, {
        toRoomId: formData.toRoomId,
        reason: formData.reason || undefined,
        billedRatePerDay: formData.billedRatePerDay,
      });
      showNotification("success", "Room transfer recorded", "", "topRight");
      onSave({
        toRoomId: formData.toRoomId,
        reason: formData.reason,
        billedRatePerDay: formData.billedRatePerDay,
        transferDate: new Date().toISOString(),
      });
    } catch (error: any) {
      const msg = error?.response?.data?.error?.title || error?.response?.data?.message || "Failed to transfer room";
      showNotification("error", msg, "", "topRight");
    } finally {
      setSaving(false);
    }
  };

  const defaultRate = selectedRoom?.roomType?.pricePerDay ?? null;
  const hasMismatch =
    formData.billedRatePerDay !== null &&
    defaultRate !== null &&
    formData.billedRatePerDay !== defaultRate;

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">From Room</label>
        <input
          type="text"
          value={currentRoom ? `${currentRoom.name} (${currentRoom.roomType?.name ?? "N/A"})` : "No room assigned"}
          disabled
          className="block mt-1 w-full bg-gray-100 rounded-md border-gray-300 shadow-sm cursor-not-allowed sm:text-sm"
        />
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          To Room <span className="text-red-500">*</span>
        </label>
        <Select
          style={{ width: "100%" }}
          placeholder="Select destination room"
          value={formData.toRoomId}
          onChange={handleRoomSelect}
          loading={loading}
          showSearch
          filterOption={(input, option) => {
            const text = typeof option?.children === "string" ? option.children : String(option?.children ?? "");
            return text.toLowerCase().includes(input.toLowerCase());
          }}
        >
          {availableRooms.map((room) => (
            <SelectOption key={room.id} value={room.id}>
              {room.name} ({room.roomType?.name ?? "N/A"})
            </SelectOption>
          ))}
        </Select>
        {availableRooms.length === 0 && !loading && (
          <p className="mt-1 text-sm text-gray-500">No available rooms found</p>
        )}
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Billed Rate (per day, ₹)
        </label>
        <InputNumber
          style={{ width: "100%" }}
          min={0}
          precision={2}
          placeholder={defaultRate !== null ? `Default: ₹${defaultRate}` : "Enter rate"}
          value={formData.billedRatePerDay ?? undefined}
          onChange={(val) => setFormData((prev) => ({ ...prev, billedRatePerDay: val ?? null }))}
        />
        {hasMismatch && (
          <p className="mt-1 text-xs text-amber-600">
            Note: Billed rate (₹{formData.billedRatePerDay}) differs from room type default (₹{defaultRate}). This will be highlighted in billing.
          </p>
        )}
        {defaultRate !== null && !hasMismatch && formData.toRoomId && (
          <p className="mt-1 text-xs text-gray-400">Matches room type default rate</p>
        )}
      </div>

      <div>
        <label className="block mb-1 text-sm font-medium text-gray-700">Reason for Transfer</label>
        <textarea
          value={formData.reason}
          onChange={(e) => setFormData((prev) => ({ ...prev, reason: e.target.value }))}
          rows={3}
          className="block mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50 sm:text-sm"
          placeholder="Reason for room transfer..."
        />
      </div>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Save Transfer"}
        </button>
      </div>
    </form>
  );
};

export default RoomTransferForm;
